import 'primary/events/components/matchs/edit/ModalEditScore.scss';

import { TextField } from '@mui/material';
import { IMatchRepository } from 'domain/event/match/Match.repository';
import { UnpyMatch } from 'domain/event/match/UnpyMatch';
import { SystemPointsEnum } from 'domain/event/SystemPoints.enum';
import { formatDate } from 'helper/date.helper';
import { CoupleButtonValidCancel } from 'primary/Components/CoupleButtonValidCancel/CoupleButtonValidCancel';
import { InputWrapper } from 'primary/Components/Input/InputWrapper';
import { sendEventToastMessage } from 'primary/Components/Toast/Toast.helper';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useTranslate, UseTranslateReturn } from 'primary/hooks/useTranslate';
import { useYupValidationResolver } from 'primary/hooks/useYupValidationResolver';
import ModalContainer from 'primary/ModalContainer/ModalContainer';
import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

interface ModalEditDetailsMatchBRProps {
  isOpen: boolean;
  handleClose: () => void;
  systemPoint: SystemPointsEnum;
  onValidEdit: () => void;
  match: UnpyMatch;
  retry: () => void;
}

export type MatchUpdateDetailsFormData = {
  id: number;
  startDate?: string;
};
const validationSchema = (t: UseTranslateReturn) =>
  yup.object({
    id: yup.number().required(t('general.forms.errors.required')),
    startDate: yup.string().nullable(),
  });
const useOnSubmit =
  (
    matchRepository: IMatchRepository,
    onSubmitted: () => void,
    setLoadingState: (loadingState: boolean) => void,
    t: UseTranslateReturn,
  ) =>
  (data: MatchUpdateDetailsFormData) => {
    setLoadingState(true);
    matchRepository
      .updateDetails(data)
      .then(() => {
        onSubmitted();
        sendEventToastMessage(t('event.matchs.edit.success'), 'success');
      })
      .finally(() => {
        setLoadingState(false);
      });
  };
export const ModalEditDetailsMatchBR: FC<ModalEditDetailsMatchBRProps> = ({
  isOpen,
  handleClose,
  systemPoint,
  onValidEdit,
  match,
  retry,
}) => {
  const { matchsRepository } = useContextDependency();
  const t = useTranslate();
  const resolver = useYupValidationResolver(validationSchema);
  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    register,
    getValues,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<MatchUpdateDetailsFormData>({
    resolver,
    defaultValues: {
      id: match.id,
      startDate: match.startDate
        ? formatDate(match.startDate, 'inputDatetime')
        : undefined,
    },
  });
  const [loadingSubmit, setLoadingSubmit] = React.useState(false);
  const onSubmit = useOnSubmit(matchsRepository, retry, setLoadingSubmit, t);
  return (
    <ModalContainer
      handleClose={handleClose}
      isOpen={isOpen}
      header={{
        title: t('event.matchs.edit.modal.title'),
      }}
      content={
        <div className={'modal-match-edit'}>
          <form onSubmit={handleSubmit(onSubmit)} className={'-formContainer'}>
            <div className={'-fieldContainer'}>
              <div className={'-details'}>
                <h3>{t('event.matchs.edit.modal.detailsTitle')}</h3>
                <InputWrapper
                  className={'unpy-form__field'}
                  label={t('event.matchs.edit.modal.startDateLabel')}
                  errors={errors}
                  isSubmitting={isSubmitting}
                  Input={TextField}
                  inputProps={{
                    placeholder: t('event.matchs.edit.modal.startDateLabel'),
                    variant: 'filled',
                    disabled: isSubmitting,
                    ...register('startDate'),
                    type: 'datetime-local',
                  }}
                />
              </div>
            </div>
            <CoupleButtonValidCancel
              isTypeSubmit
              loading={loadingSubmit}
              onClickCancel={handleClose}
            />
          </form>
        </div>
      }
    />
  );
};
